import Styles from './ReviewSent.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { useShareSheetContext } from '../ShareSheetV2'
import { RecipientsSelectorValueType, TextChannelKey, VisualChannelKey } from 'types'
import { UserValue, EmailValue } from 'common/RecipientBadge/types'
import { RecipientAvatars, UserAvatar } from '../RecipientAvatars/RecipientAvatars'
import { pluralize } from 'utils'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import { TextChannels } from '../../../constants'
import { lowerCase } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ViewSummaryButton } from 'components/DocRightRail/screens/AIAssistant/ReviewRequestCard/Buttons/ViewSummaryButton'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useIsNotSelf } from 'hooks/useIsNotSelf'
import { DesignChannelsNames } from './constants'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export const ReviewSent = observer(({ request }: { request?: InformalFeedbackRequest }) => {
  const { file } = useShareSheetContext()
  const { asset } = useShareSheetV2(file)
  const { recipients } = useShareSheetV2(file)
  const { closeAllModals } = useModals()
  const { drawersStore } = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const currentWorkspace = useCurrentWorkspace()
  const isNotSelf = useIsNotSelf()
  const workspaceMembers = useWorkspaceMembers(currentWorkspace?.key, { includeInactive: false })

  const users = (recipients.filter(({ type }) => type === RecipientsSelectorValueType.User) as UserValue[]).map(
    (recipient) => {
      return {
        avatar_url: recipient.value.avatar_url,
        display_name: recipient.value.display_name,
      }
    },
  ) as UserAvatar[]

  const guests = (recipients.filter(({ type }) => type === RecipientsSelectorValueType.Email) as EmailValue[]).map(
    (recipient) => {
      return {
        display_name: recipient.value,
      }
    },
  ) as UserAvatar[]

  // Set all workspace members as reviewers if workspace group selected
  let workspaceReviewers: UserAvatar[] = []
  if (recipients.find((rec) => rec.type === RecipientsSelectorValueType.Company)) {
    workspaceReviewers = workspaceMembers
      .filter(
        (member) =>
          isNotSelf(member) &&
          !recipients.find((rec) => rec.type === RecipientsSelectorValueType.User && rec.value.id === member.id),
      )
      .map((member) => ({
        avatar_url: member.avatar_url || '',
        display_name: member.display_name,
      }))
  }

  const userReviewers = users.concat(guests).concat(workspaceReviewers)

  let assetName: string = 'post'
  if (asset?.type === 'article') {
    assetName = asset?.channel
      ? lowerCase(TextChannels[asset.channel as TextChannelKey].displayName) || 'document'
      : 'document'
  } else if (asset?.type === 'design') {
    assetName = request?.design?.channel
      ? DesignChannelsNames[request.design.channel as VisualChannelKey] || 'post'
      : 'post'
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.body}>
        <RecipientAvatars users={userReviewers} />
        <Heading level={3}>
          {userReviewers.length} {pluralize('invite', userReviewers.length)} to review{' '}
          {userReviewers.length > 1 ? 'have' : 'has'} been sent successfully!
        </Heading>
        <div className={Styles.label}>
          <Text variant="secondary" color="var(--dark-80)">
            Blaze will send reminders to the reviewers to look at your {assetName} as the due date approaches.{' '}
          </Text>
        </div>
      </div>
      <div className={Styles.footer}>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <ViewSummaryButton request={request} size={isMobile ? 'xl' : 'lg'} variant="tertiary" />
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button
              size={isMobile ? 'xl' : 'lg'}
              variant="primary"
              onPress={() => {
                closeAllModals()
                drawersStore.closeDrawer()
              }}
            >
              Close
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </div>
    </div>
  )
})
